import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getWorkspaceTokens } from './workspaceStyles';
import { getControlTokens } from '../controls';

const FILTER_LABEL_MODE_BUFFER_PX = 12;

const measureElementWidth = (element) => {
  if (!element) return 0;
  const rectWidth = element.getBoundingClientRect?.().width || 0;
  return rectWidth || element.scrollWidth || element.offsetWidth || 0;
};

const getNumericGap = (element, fallback = 8) => {
  if (!element || typeof window === 'undefined' || !window.getComputedStyle) return fallback;
  const styles = window.getComputedStyle(element);
  const rawGap = styles.columnGap || styles.gap;
  const parsed = Number.parseFloat(rawGap);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const measureControlsNaturalWidth = (content) => {
  if (!content) return 0;
  const children = Array.from(content.children);
  if (children.length === 0) return measureElementWidth(content);
  const gap = getNumericGap(content);
  return children.reduce((total, child) => total + measureElementWidth(child), 0)
    + Math.max(0, children.length - 1) * gap;
};

function WorkspaceFilterHost({
  open = true,
  filterBar,
  summaryLabels = [],
  exportFilters = [],
  label = 'Filters',
  hideLabel = false,
  maxControlWidth = null,
  disabled = false,
}) {
  const theme = useTheme();
  const tokens = getWorkspaceTokens(theme);
  const controlTokens = getControlTokens(theme, 'objectToolbar');
  const rootRef = useRef(null);
  const [labelMode, setLabelMode] = useState('inline');
  const [filterLayout, setFilterLayout] = useState('inline');
  const hostMinHeight = open
    ? Math.max(tokens.controlHeight, controlTokens.height + 14)
    : Math.max(tokens.controlHeight, controlTokens.height);
  const controlClampSx = maxControlWidth == null ? {} : {
    '& .MuiFormControl-root, & .MuiAutocomplete-root': {
      maxWidth: maxControlWidth,
      flexShrink: 0,
    },
    '& .MuiToggleButtonGroup-root': {
      maxWidth: maxControlWidth,
      flexWrap: 'nowrap',
      flexShrink: 0,
    },
  };

  const measureLabelMode = useCallback(() => {
    const root = rootRef.current;
    if (!root || !open) {
      setFilterLayout('inline');
      setLabelMode('inline');
      return;
    }

    const parentLayout = root.parentElement?.getAttribute('data-workspace-filter-layout') || 'inline';
    const content = root.querySelector('[data-workspace-filter-content="true"]');
    const label = root.querySelector('[data-testid="workspace-filter-label"]');
    const rootWidth = root.clientWidth || measureElementWidth(root);
    const labelWidth = measureElementWidth(label);
    const controlsWidth = measureControlsNaturalWidth(content);
    const inlineGap = getNumericGap(root);
    const inlineWidth = labelWidth + (labelWidth && controlsWidth ? inlineGap : 0) + controlsWidth;
    setFilterLayout((currentLayout) => (currentLayout === parentLayout ? currentLayout : parentLayout));
    setLabelMode((currentMode) => {
      if (parentLayout !== 'stacked' || rootWidth <= 0) return 'inline';
      if (currentMode === 'top') {
        return inlineWidth <= rootWidth - FILTER_LABEL_MODE_BUFFER_PX ? 'inline' : 'top';
      }
      return inlineWidth > rootWidth ? 'top' : 'inline';
    });
  }, [open]);

  useLayoutEffect(() => {
    measureLabelMode();

    if (!open) return undefined;

    const cleanups = [];
    const root = rootRef.current;
    const content = root?.querySelector('[data-workspace-filter-content="true"]');
    const observedNodes = [root, content].filter(Boolean);

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        measureLabelMode();
      });
      observedNodes.forEach((node) => resizeObserver.observe(node));
      cleanups.push(() => resizeObserver.disconnect());
    }

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [measureLabelMode, open]);

  if (!open) return null;

  const labelAbove = labelMode === 'top';
  const allowInternalWrap = filterLayout === 'stacked';

  return (
    <Box
      ref={rootRef}
      aria-hidden={disabled || undefined}
      inert={disabled ? '' : undefined}
      data-testid="workspace-filter-host"
      data-workspace-filter-label-mode={labelMode}
      data-chart-export-filter-labels={JSON.stringify(summaryLabels)}
      data-chart-export-filters={JSON.stringify(exportFilters)}
      sx={{
        display: 'flex',
        flexDirection: labelAbove ? 'column' : 'row',
        alignItems: labelAbove ? 'stretch' : 'center',
        // Keep overflowing controls reachable from the start on narrow screens.
        justifyContent: labelAbove ? 'flex-start' : 'safe center',
        flexWrap: 'nowrap',
        gap: labelAbove ? 0.35 : 1,
        flex: '1 1 auto',
        minWidth: 0,
        maxWidth: '100%',
        width: '100%',
        minHeight: hostMinHeight,
        pt: 0,
        color: theme.palette.text.primary,
        backgroundColor: 'transparent',
        overflowX: labelAbove ? 'hidden' : 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        filter: disabled ? 'blur(4px) saturate(0.9)' : 'none',
        pointerEvents: disabled ? 'none' : 'auto',
        userSelect: disabled ? 'none' : 'auto',
        '& [data-workspace-filter-bar="true"]': {
          width: labelAbove ? '100%' : 'fit-content',
          maxWidth: labelAbove ? '100%' : 'none',
        },
        '& [data-workspace-filter-content="true"]': {
          flexWrap: allowInternalWrap ? 'wrap' : 'nowrap',
          justifyContent: 'safe center',
        },
        '& [data-filter-sublabel="true"]': labelAbove ? {
          fontSize: '7.5px',
          lineHeight: 1,
          letterSpacing: '0.15px',
          pt: 0.35,
          mt: 0,
          minHeight: 0,
        } : undefined,
        '& [data-control-context="objectToolbar"]': labelAbove ? {
          minHeight: controlTokens.height,
        } : undefined,
      }}
    >
      {!hideLabel && (
        <Box
          data-testid="workspace-filter-label"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: controlTokens.gap,
            flex: '0 0 auto',
            alignSelf: 'center',
            minHeight: labelAbove ? 'auto' : controlTokens.height,
            color: theme.palette.text.secondary,
            fontSize: labelAbove ? 9.5 : 11,
            fontWeight: 800,
            lineHeight: labelAbove ? 1.05 : 1,
            textTransform: 'uppercase',
            mb: labelAbove ? 0.2 : 0,
          }}
        >
          {label}
        </Box>
      )}

      <Box
        data-testid="workspace-filter-bar-frame"
        sx={{
          flex: labelAbove ? '1 1 auto' : '0 0 auto',
          minWidth: 0,
          maxWidth: labelAbove ? '100%' : 'none',
          width: labelAbove ? '100%' : 'fit-content',
          overflow: 'visible',
          backgroundColor: 'transparent',
          color: theme.palette.text.primary,
          ...controlClampSx,
          '& .MuiDivider-root': {
            flexShrink: 0,
          },
          '& [data-workspace-collapse-toggle="true"]': {
            display: 'none',
          },
        }}
      >
        {filterBar}
      </Box>
    </Box>
  );
}

export default WorkspaceFilterHost;
